'use client'

import type * as React from 'react'
import { useCallback, useState } from 'react'
import { AUTO_SAVE_FEEDBACK_MS, useAutoSave } from '@/hooks/use-auto-save'
import { cn } from '@/lib/utils'

interface AutoSaveToggleProps {
  /** Current checked state */
  checked: boolean
  /**
   * Called to save the new value — should return a promise.
   *
   * Must reject on failure, and must leave `checked` holding the persisted
   * value by the time it does. The switch shows `checked` again as soon as the
   * save settles, so a caller that applies an optimistic update and does not
   * roll it back leaves the switch displaying a value the server refused,
   * looking exactly like a save that worked.
   */
  onSave: (checked: boolean) => Promise<void>
  /** Optional label for the toggle */
  label?: string
  /** Whether the toggle is disabled */
  disabled?: boolean
  /** Duration in ms for the success/error indicator to show */
  feedbackDurationMs?: number
  /** Additional class names */
  className?: string
  /** Aria label for accessibility */
  'aria-label'?: string
}

/**
 * A toggle component that auto-saves on change with visual feedback.
 * Shows a green glow on successful save, and a red glow on error.
 * The toggle is disabled while saving to prevent rapid toggles.
 *
 * It does not revert a failed save on its own — it goes back to showing
 * `checked`, which its caller is required to have restored. See `onSave`.
 */
const AutoSaveToggle = ({
  checked,
  onSave,
  label,
  disabled = false,
  feedbackDurationMs = AUTO_SAVE_FEEDBACK_MS,
  className,
  'aria-label': ariaLabel,
}: AutoSaveToggleProps) => {
  const [isPressed, setIsPressed] = useState(false)
  const [attempted, setAttempted] = useState(checked)

  const { state: saveState, flush } = useAutoSave<boolean>({
    onSave,
    feedbackMs: feedbackDurationMs,
    logLabel: 'AutoSaveToggle',
  })

  /**
   * What the switch shows: the value being saved while a save is in flight or
   * flashing, and the caller's value the rest of the time.
   *
   * Derived here rather than copied into state, because a copy of the truth can
   * disagree with the truth. The error path used to revert by negating the
   * value it had attempted, which is the same answer only when the caller
   * restored exactly what the switch tried to change — and marking a problem's
   * original source is not that: it is a two-row edit whose undo can put back a
   * value that is neither the attempt nor its opposite. The switch then read
   * OFF, wearing the error glow, beside an ORIGINAL badge on the same card.
   *
   * Reading `checked` on `error` is what makes `onSave`'s restore precondition
   * load-bearing. `saved` reads the attempt instead: the caller's state has not
   * always landed by the time the promise resolves, and the success flash must
   * not blink back through the old value on its way there.
   */
  const displayChecked =
    saveState === 'saving' || saveState === 'saved' ? attempted : checked

  const handleToggle = useCallback(() => {
    if (disabled || saveState === 'saving') return

    const newValue = !displayChecked
    setAttempted(newValue)
    // A toggle is a deliberate discrete action — save it now, no debounce.
    flush(newValue)
  }, [disabled, saveState, displayChecked, flush])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleToggle()
    }
  }

  const isDisabled = disabled || saveState === 'saving'

  // Determine colors based on save state
  const getTrackColor = () => {
    if (saveState === 'error') {
      return 'bg-red-500'
    }
    if (saveState === 'saved') {
      return displayChecked ? 'bg-green-600' : 'bg-green-500/30'
    }
    if (saveState === 'saving') {
      return displayChecked ? 'bg-blue-500' : 'bg-blue-500/30'
    }
    return displayChecked ? 'bg-foreground' : 'bg-muted-foreground/20'
  }

  const getGlowEffect = () => {
    if (saveState === 'error') {
      return 'shadow-[0_0_20px_rgba(239,68,68,0.4)]'
    }
    if (saveState === 'saved') {
      return 'shadow-[0_0_20px_rgba(34,197,94,0.4)]'
    }
    if (saveState === 'saving') {
      return 'shadow-[0_0_20px_rgba(59,130,246,0.4)]'
    }
    return displayChecked ? 'shadow-[0_0_20px_rgba(0,0,0,0.15)]' : ''
  }

  const getDotColor = () => {
    if (saveState === 'error') {
      return 'bg-red-500'
    }
    if (saveState === 'saved') {
      return 'bg-green-500'
    }
    if (saveState === 'saving') {
      return 'bg-blue-500 animate-pulse'
    }
    return displayChecked ? 'bg-foreground' : 'bg-muted-foreground/40'
  }

  const getLabelColor = () => {
    if (saveState === 'error') {
      return 'text-red-500'
    }
    if (saveState === 'saved') {
      return 'text-green-600'
    }
    if (saveState === 'saving') {
      return 'text-blue-500'
    }
    return displayChecked ? 'text-foreground' : 'text-muted-foreground'
  }

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {label && (
        <span
          className={cn(
            'text-sm font-medium transition-colors duration-300',
            getLabelColor(),
          )}
        >
          {label}
        </span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={displayChecked}
        aria-label={ariaLabel}
        aria-disabled={isDisabled}
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        onMouseDown={() => !isDisabled && setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onMouseLeave={() => setIsPressed(false)}
        disabled={isDisabled}
        className={cn(
          'group relative h-8 w-14 rounded-full p-1 transition-all duration-500 ease-out',
          'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
          getTrackColor(),
          isDisabled && 'cursor-not-allowed opacity-70',
        )}
      >
        {/* Glow effect */}
        <div
          className={cn(
            'absolute inset-0 rounded-full transition-all duration-500',
            saveState !== 'idle' || displayChecked
              ? 'opacity-100'
              : 'opacity-0',
            getGlowEffect(),
          )}
        />

        {/* Track inner gradient */}
        <div
          className={cn(
            'absolute inset-[2px] rounded-full transition-all duration-500',
            displayChecked && saveState === 'idle'
              ? 'from-foreground to-foreground/90 bg-gradient-to-b'
              : 'bg-transparent',
            saveState === 'saved' &&
              displayChecked &&
              'bg-gradient-to-b from-green-600 to-green-700',
            saveState === 'error' && 'bg-gradient-to-b from-red-500 to-red-600',
            saveState === 'saving' &&
              displayChecked &&
              'bg-gradient-to-b from-blue-500 to-blue-600',
          )}
        />

        {/* Thumb */}
        <div
          className={cn(
            'relative h-6 w-6 rounded-full shadow-lg transition-all duration-500 ease-[cubic-bezier(0.68,-0.55,0.265,1.55)]',
            'bg-background',
            displayChecked ? 'translate-x-6' : 'translate-x-0',
            isPressed && !isDisabled && 'scale-90 duration-150',
            saveState === 'saving' && 'scale-95',
          )}
        >
          {/* Thumb inner shine */}
          <div className="from-background via-background to-muted/30 absolute inset-[2px] rounded-full bg-gradient-to-b" />

          {/* Thumb highlight */}
          <div className="from-background/80 absolute inset-0 rounded-full bg-gradient-to-br via-transparent to-transparent" />

          {/* Status indicator dot */}
          <div
            className={cn(
              'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-500',
              displayChecked || saveState !== 'idle'
                ? 'h-2 w-2 scale-100'
                : 'h-1.5 w-1.5 scale-100',
              getDotColor(),
            )}
          />

          {/* Ripple effect on toggle */}
          {saveState === 'saved' && (
            <div
              className="absolute inset-0 animate-ping rounded-full bg-green-500/20 opacity-0"
              key={`saved-${displayChecked}`}
            />
          )}
          {saveState === 'error' && (
            <div
              className="absolute inset-0 animate-ping rounded-full bg-red-500/30 opacity-0"
              key={`error-${displayChecked}`}
            />
          )}
        </div>
      </button>
    </div>
  )
}

export { AutoSaveToggle }
export type { AutoSaveToggleProps }
